import { useCallback, useEffect, useState } from 'react';
import { GeolocationService } from '../services/geolocation.service';
import {
    getInactivePlotsNearMe,
    getOutlierPlotsNearMe,
} from '../services/plot.service';

const MAX_DISTANCE_KM = 100;
const LIMIT = 150;

export type NearMeListType = 'inactive' | 'outlier';

export interface NearMePlots {
    enabled: boolean;
    loading: boolean;
    error: string | null;
    plots: any[];
    maxDistanceKm: number;
    limit: number;
    /** true when the backend returned a full page — there may be more nearby plots than shown */
    truncated: boolean;
    setEnabled: (value: boolean) => void;
    retry: () => void;
}

/**
 * Owns the "Show plots near me" mode for the Faulty Plots page: reads the
 * device location once (per activation / retry) and fetches the nearest
 * inactive/outlier plots, already sorted by distance.
 */
export default function useNearMePlots(
    userProfile: any,
    listType: NearMeListType,
): NearMePlots {
    const [enabled, setEnabledState] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [plots, setPlots] = useState<any[]>([]);
    const [nonce, setNonce] = useState(0);

    const setEnabled = useCallback((value: boolean) => {
        setEnabledState(value);
        if (!value) {
            setPlots([]);
            setError(null);
            setLoading(false);
        }
    }, []);

    const retry = useCallback(() => setNonce((n) => n + 1), []);

    useEffect(() => {
        if (!enabled) return;

        // Wait for the profile so region scoping is applied on the first fetch
        // (getInactivePlotsNearMe reads userProfile.regionIds). The effect
        // re-runs when userProfile arrives.
        if (!userProfile) {
            setLoading(true);
            return;
        }

        let mounted = true;
        setLoading(true);
        setError(null);

        (async () => {
            try {
                const position = await new GeolocationService().getCurrentPosition();
                const coords = position?.coords;
                if (!coords?.latitude || !coords?.longitude) {
                    throw new Error(
                        'Could not read your location. Check GPS and try again.',
                    );
                }

                const fetcher =
                    listType === 'inactive'
                        ? getInactivePlotsNearMe
                        : getOutlierPlotsNearMe;

                const results = await fetcher(
                    userProfile,
                    { latitude: coords.latitude, longitude: coords.longitude },
                    MAX_DISTANCE_KM,
                    LIMIT,
                );

                if (mounted) setPlots(Array.isArray(results) ? results : []);
            } catch (err: any) {
                if (!mounted) return;
                setPlots([]);
                setError(err?.message || 'Failed to fetch nearby plots.');
            } finally {
                if (mounted) setLoading(false);
            }
        })();

        return () => {
            mounted = false;
        };
    }, [enabled, listType, userProfile, nonce]);

    return {
        enabled,
        loading,
        error,
        plots,
        maxDistanceKm: MAX_DISTANCE_KM,
        limit: LIMIT,
        truncated: plots.length >= LIMIT,
        setEnabled,
        retry,
    };
}
